import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { BASE_URL } from "../api";

const API = `${BASE_URL}/api`;

/* ─── Design Tokens ─────────────────────────────────────────────── */
const T = {
  navy:    "#0D1F3C",
  blue:    "#1A4F8A",
  sky:     "#3B82F6",
  skyHov:  "#2563EB",
  purple:  "#7C3AED",
  green:   "#16A34A",
  amber:   "#D97706",
  red:     "#DC2626",
  slate:   "#475569",
  steel:   "#64748B",
  mist:    "#F1F5F9",
  white:   "#FFFFFF",
  border:  "#E2E8F0",
  shadow:  "0 4px 24px rgba(13,31,60,0.10)",
  card:    "0 1px 4px rgba(13,31,60,0.07)",
  radius:  "12px",
  radius2: "8px",
};

const BILLING_MODES = [
  { value: "SERVICE",      label: "Service Level MG",  icon: "🔬", color: T.sky,    desc: "Per-service minimum guarantee with daily MG tracking" },
  { value: "PACKAGE",      label: "Package Level MG",  icon: "📦", color: T.purple, desc: "Package-based billing with package-level MG" },
  { value: "CAMP_CASE",    label: "Camp Case MG",      icon: "🗂️", color: T.green,  desc: "Minimum guaranteed cases across the entire camp" },
  { value: "CAMP_AMOUNT",  label: "Camp Amount MG",    icon: "💰", color: T.amber,  desc: "Minimum guaranteed billing amount for the camp" },
  { value:"MEDIBUDDY",  label:"Medibuddy Billing",     icon:"🏥",  color:"#0EA5E9", desc:"Dynamic slab case billing + camp day charges"},
  { value: "TCS_MONTHLY",label: "TCS Monthly Billing",icon: "🏢",color: "#2563EB",desc: "Monthly billing with configurable service-wise rates",},
];

/* ─── Global Styles ─────────────────────────────────────────────── */
const GlobalStyle = () => {
  useEffect(() => {
    const id = "camps-global-style-v2";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .camps-root {
        font-family: 'Sora', sans-serif;
        background: #F8FAFD;
        min-height: 100vh;
        padding: 36px 28px;
        color: ${T.navy};
      }

      /* ── Header ── */
      .camps-header {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 28px; gap: 16px; flex-wrap: wrap;
      }
      .camps-title {
        font-size: 26px; font-weight: 700; letter-spacing: -0.5px;
        color: ${T.navy}; display: flex; align-items: center; gap: 10px;
      }
      .camps-title-dot {
        width: 10px; height: 10px; border-radius: 50%;
        background: ${T.sky}; display: inline-block;
      }
      .breadcrumb {
        display: flex; align-items: center; gap: 8px;
        font-size: 13px; color: ${T.steel}; margin-bottom: 20px; flex-wrap: wrap;
      }
      .breadcrumb-sep { color: ${T.border}; font-size: 16px; }
      .breadcrumb-link {
        color: ${T.sky}; cursor: pointer; font-weight: 600;
        text-decoration: none; transition: color 0.15s;
      }
      .breadcrumb-link:hover { color: ${T.skyHov}; }
      .breadcrumb-current { color: ${T.navy}; font-weight: 600; }

      /* ── Controls / Filters ── */
      .camps-controls {
        display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
      }
      .camps-search {
        border: 1.5px solid ${T.border}; border-radius: ${T.radius2};
        padding: 9px 14px; font-family: 'Sora', sans-serif; font-size: 14px;
        color: ${T.navy}; background: ${T.white}; outline: none; width: 220px;
        transition: border-color 0.2s;
      }
      .camps-search:focus { border-color: ${T.sky}; }
      .camps-search::placeholder { color: ${T.steel}; }
      .filter-select {
        border: 1.5px solid ${T.border}; border-radius: ${T.radius2};
        padding: 9px 14px; font-family: 'Sora', sans-serif; font-size: 13px;
        color: ${T.navy}; background: ${T.white}; outline: none;
        cursor: pointer; transition: border-color 0.2s;
      }
      .filter-select:focus { border-color: ${T.sky}; }

      /* ── Buttons ── */
      .btn {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 9px 16px; border: none; border-radius: ${T.radius2};
        font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600;
        cursor: pointer; transition: filter 0.15s, transform 0.1s;
        white-space: nowrap; line-height: 1;
      }
      .btn:hover { filter: brightness(1.1); }
      .btn:active { transform: scale(0.97); }
      .btn-primary  { background: ${T.blue};   color: #fff; }
      .btn-sky      { background: ${T.sky};    color: #fff; }
      .btn-purple   { background: ${T.purple}; color: #fff; }
      .btn-green    { background: ${T.green};  color: #fff; }
      .btn-amber    { background: ${T.amber};  color: #fff; }
      .btn-slate    { background: ${T.slate};  color: #fff; }
      .btn-red      { background: ${T.red};    color: #fff; }
      .btn-ghost    { background: ${T.mist};   color: ${T.navy}; border: 1.5px solid ${T.border}; }
      .btn-sm { padding: 6px 12px; font-size: 12px; }
      .btn-xs { padding: 4px 9px; font-size: 11px; }

      /* ── Table ── */
      .camps-table-wrap {
        background: ${T.white}; border-radius: ${T.radius};
        box-shadow: ${T.shadow}; overflow-x: auto; overflow-y: hidden; border: 1px solid ${T.border};
      }
      .camps-table { width: 100%; border-collapse: collapse; font-size: 14px; }
      .camps-table thead { background: ${T.navy}; color: #fff; }
      .camps-table thead th {
        padding: 14px 16px; text-align: left; font-weight: 600;
        font-size: 12px; letter-spacing: 0.6px; text-transform: uppercase; white-space: nowrap;
      }
      .camps-table tbody tr { border-bottom: 1px solid ${T.border}; transition: background 0.15s; }
      .camps-table tbody tr:last-child { border-bottom: none; }
      .camps-table tbody tr:hover { background: ${T.mist}; }
      .camps-table tbody td { padding: 14px 16px; vertical-align: middle; }
      .camp-name { font-weight: 600; color: ${T.navy}; font-size: 14px; }
      .camp-location { font-size: 12px; color: ${T.steel}; margin-top: 2px; font-family: 'DM Mono', monospace; }
      .date-range { font-size: 12px; font-family: 'DM Mono', monospace; color: ${T.slate}; white-space: nowrap; }

      /* ── Badges ── */
      .badge {
        display: inline-flex; align-items: center; padding: 3px 10px;
        border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
      }
      .badge-sky    { background: #EFF6FF; color: ${T.sky};    border: 1px solid #BFDBFE; }
      .badge-red {
          background: #FEF2F2;
          color: #DC2626;
          border: 1px solid #FECACA;
      }
      .badge-purple { background: #F5F3FF; color: ${T.purple}; border: 1px solid #DDD6FE; }
      .badge-green  { background: #F0FDF4; color: ${T.green};  border: 1px solid #BBF7D0; }
      .badge-amber  { background: #FFFBEB; color: ${T.amber};  border: 1px solid #FDE68A; }
      .badge-slate  { background: ${T.mist}; color: ${T.slate}; border: 1px solid ${T.border}; }
      .badge-active  { background: #F0FDF4; color: ${T.green};  border: 1px solid #BBF7D0; }
      .badge-closed  { background: #FFF1F2; color: ${T.red};    border: 1px solid #FECDD3; }

      /* ── Action group ── */
      .action-group { display: flex; gap: 6px; flex-wrap: wrap; }

      /* ── Toast ── */
      .toast {
        position: fixed; top: 24px; right: 24px; z-index: 9999;
        color: #fff; padding: 14px 20px; border-radius: ${T.radius};
        font-size: 14px; font-weight: 500; box-shadow: 0 8px 32px rgba(13,31,60,0.22);
        animation: slideIn 0.25s ease; display: flex; align-items: center; gap: 8px; max-width: 320px;
      }
      .toast-success { background: ${T.green}; }
      .toast-error   { background: ${T.red};   }
      .toast-info    { background: ${T.navy};  }
      @keyframes slideIn {
        from { opacity: 0; transform: translateX(30px); }
        to   { opacity: 1; transform: translateX(0); }
      }

      /* ── Modal ── */
      .modal-overlay {
        position: fixed; inset: 0; background: rgba(13,31,60,0.42);
        backdrop-filter: blur(4px); display: flex; align-items: center;
        justify-content: center; z-index: 1000; padding: 20px; animation: fadeIn 0.2s ease;
      }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      .modal-box {
        background: ${T.white}; border-radius: ${T.radius};
        box-shadow: 0 20px 60px rgba(13,31,60,0.20); width: 100%;
        max-width: 680px; max-height: 90vh; overflow-y: auto;
        animation: popIn 0.22s cubic-bezier(0.34,1.56,0.64,1);
      }
      @keyframes popIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
      .modal-box-wide { max-width: 860px; }
      .modal-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 20px 24px 16px; border-bottom: 1px solid ${T.border};
        position: sticky; top: 0; background: #fff; z-index: 1;
      }
      .modal-title { font-size: 18px; font-weight: 700; color: ${T.navy}; }
      .modal-close {
        width: 32px; height: 32px; border-radius: 8px; border: none;
        background: ${T.mist}; cursor: pointer; font-size: 18px;
        display: flex; align-items: center; justify-content: center;
        color: ${T.steel}; transition: background 0.15s;
      }
      .modal-close:hover { background: ${T.border}; }
      .modal-body { padding: 24px; }
      .modal-footer {
        padding: 16px 24px; border-top: 1px solid ${T.border};
        display: flex; gap: 10px; justify-content: flex-end;
      }

      /* ── Form ── */
      .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .form-group { display: flex; flex-direction: column; gap: 6px; }
      .form-group.full { grid-column: 1 / -1; }
      .form-label { font-size: 12px; font-weight: 600; color: ${T.slate}; letter-spacing: 0.3px; text-transform: uppercase; }
      .form-input, .form-select {
        border: 1.5px solid ${T.border}; border-radius: ${T.radius2};
        padding: 9px 12px; font-family: 'Sora', sans-serif; font-size: 14px;
        color: ${T.navy}; background: ${T.white}; outline: none;
        transition: border-color 0.2s, box-shadow 0.2s; width: 100%;
      }
      .form-input:focus, .form-select:focus {
        border-color: ${T.sky}; box-shadow: 0 0 0 3px rgba(59,130,246,0.12);
      }
      .form-hint { font-size: 11px; color: ${T.steel}; margin-top: 2px; }

      /* ── Billing Mode Cards ── */
      .mode-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 24px; }
      .mode-card {
        border: 2px solid ${T.border}; border-radius: ${T.radius};
        padding: 18px; cursor: pointer; transition: all 0.2s; position: relative;
        background: ${T.white};
      }
      .mode-card:hover { border-color: ${T.sky}; background: #F8FBFF; transform: translateY(-1px); }
      .mode-card.selected { border-color: var(--mode-color); background: var(--mode-bg); box-shadow: 0 0 0 3px var(--mode-ring); }
      .mode-card-icon { font-size: 24px; margin-bottom: 8px; }
      .mode-card-label { font-size: 15px; font-weight: 700; color: ${T.navy}; margin-bottom: 4px; }
      .mode-card-desc { font-size: 12px; color: ${T.steel}; line-height: 1.5; }
      .mode-card-radio {
        position: absolute; top: 14px; right: 14px;
        width: 18px; height: 18px; border-radius: 50%;
        border: 2px solid ${T.border}; background: #fff; transition: all 0.2s;
        display: flex; align-items: center; justify-content: center;
      }
      .mode-card.selected .mode-card-radio { border-color: var(--mode-color); background: var(--mode-color); }
      .mode-card-radio-dot { width: 7px; height: 7px; border-radius: 50%; background: #fff; }

      /* ── Config Panel ── */
      .config-panel {
        background: ${T.mist}; border: 1px solid ${T.border};
        border-radius: ${T.radius}; padding: 20px; margin-top: 4px;
      }
      .config-panel-title { font-size: 13px; font-weight: 700; color: ${T.navy}; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.5px; }

      /* ── Pricing Table ── */
      .pricing-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 12px; min-width: 900px; }
      .pricing-table thead th {
        background: ${T.mist}; color: ${T.slate}; font-size: 11px;
        text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 14px;
        text-align: left; border-bottom: 1px solid ${T.border};
      }
      .pricing-table tbody td { padding: 10px 14px; border-bottom: 1px solid ${T.border}; }
      .pricing-table tbody tr:last-child td { border-bottom: none; }
      .pricing-table tbody tr:hover { background: ${T.mist}; }

      /* Service Rate Table */
      .service-rate-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 12px;
      }

      .service-rate-table thead th {
          background: #F8FAFC;
          padding: 12px 18px;
          text-align: left;
          font-size: 13px;
          font-weight: 600;
          border-bottom: 1px solid #E5E7EB;
      }

      .service-rate-table tbody td {
          padding: 12px 18px;
          border-bottom: 1px solid #E5E7EB;
          vertical-align: middle;
      }

      .service-rate-table tbody tr:last-child td {
          border-bottom: none;
      }

      .service-rate-table .service-name {
          font-weight: 600;
      }

      /* Constrain the rate column itself so the input can't stretch full width */
      .service-rate-table th:last-child,
      .service-rate-table td:last-child {
          width: 130px;
      }

      /* Higher specificity than a generic .form-input so it always wins */
      .service-rate-table .service-rate-input {
          width: 100%;
          box-sizing: border-box;
      }

      .table-scroll {
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: thin;
        scrollbar-color: #CBD5E1 #F1F5F9;
      }

      /* Chrome / Edge / Safari */
      .table-scroll::-webkit-scrollbar {
        height: 10px;
      }

      .table-scroll::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 999px;
      }

      .table-scroll::-webkit-scrollbar-thumb:hover {
        background: #94A3B8;
      }

      .table-scroll::-webkit-scrollbar-track {
        background: #F1F5F9;
        border-radius: 999px;
      }

      /* ── Summary Cards ── */
      .summary-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
      .summary-card {
        background: ${T.white}; border: 1px solid ${T.border}; border-radius: ${T.radius};
        padding: 18px; box-shadow: ${T.card};
      }
      .summary-card-label { font-size: 11px; font-weight: 600; color: ${T.steel}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
      .summary-card-value { font-size: 26px; font-weight: 700; color: ${T.navy}; font-family: 'DM Mono', monospace; }
      .summary-card-sub { font-size: 12px; color: ${T.steel}; margin-top: 4px; }

      /* ── Preview Total ── */
      .preview-total {
        background: linear-gradient(135deg, ${T.navy} 0%, ${T.blue} 100%);
        color: #fff; border-radius: ${T.radius}; padding: 20px 24px; margin-top: 20px;
        display: flex; align-items: center; justify-content: space-between;
      }
      .preview-total-label { font-size: 13px; opacity: 0.75; font-weight: 500; }
      .preview-total-amount { font-size: 28px; font-weight: 700; font-family: 'DM Mono', monospace; }

      /* ── Warning Banner ── */
      .warn-banner {
        background: #FFFBEB; border: 1px solid #FDE68A; border-radius: ${T.radius2};
        padding: 12px 16px; display: flex; align-items: flex-start; gap: 10px;
        font-size: 13px; color: #92400E; margin-bottom: 16px;
      }
      .warn-banner-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }

      /* ── History ── */
      .history-item {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 16px; border-radius: ${T.radius2}; background: ${T.mist};
        margin-bottom: 8px; border: 1px solid ${T.border};
      }
      .history-version { font-size: 12px; font-weight: 600; color: ${T.steel}; text-transform: uppercase; letter-spacing: 0.3px; }
      .history-amount { font-size: 16px; font-weight: 700; color: ${T.navy}; font-family: 'DM Mono', monospace; }

      /* ── Section heading ── */
      .section-head {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 14px;
      }
      .section-title { font-size: 15px; font-weight: 700; color: ${T.navy}; }

      /* ── Empty / Loading ── */
      .empty-state { text-align: center; padding: 60px 20px; color: ${T.steel}; }
      .empty-state-icon { font-size: 44px; margin-bottom: 12px; }
      .empty-state-text { font-size: 15px; font-weight: 500; }
      .loading-row td { text-align: center; padding: 48px; color: ${T.steel}; font-size: 14px; }

      /* ── Divider ── */
      .divider { border: none; border-top: 1px solid ${T.border}; margin: 20px 0; }

      /* ── Scrollbar ── */
      .modal-box::-webkit-scrollbar { width: 6px; }
      .modal-box::-webkit-scrollbar-track { background: transparent; }
      .modal-box::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }

      /* ── Sticky footer ── */
      .sticky-footer {
        position: sticky; bottom: 0; background: ${T.white};
        border-top: 1px solid ${T.border}; padding: 14px 24px;
        display: flex; gap: 10px; justify-content: flex-end;
        z-index: 10; border-radius: 0 0 ${T.radius} ${T.radius};
      }

      /* ── Page card ── */
      .page-card {
        background: ${T.white}; border-radius: ${T.radius}; box-shadow: ${T.shadow};
        border: 1px solid ${T.border}; overflow: hidden;
      }
      .page-card-header {
        padding: 20px 24px; border-bottom: 1px solid ${T.border};
        display: flex; align-items: center; justify-content: space-between;
      }
      .page-card-title { font-size: 17px; font-weight: 700; color: ${T.navy}; }
      .page-card-body { padding: 24px; }

      @media (max-width: 768px) {
        .form-grid { grid-template-columns: 1fr; }
        .mode-cards { grid-template-columns: 1fr; }
        .summary-cards { grid-template-columns: 1fr 1fr; }
        .camps-table { font-size: 13px; }
        .camps-table thead th, .camps-table tbody td { padding: 10px 10px; }
        .camps-root { padding: 16px; }
      }
      @media (max-width: 480px) {
        .summary-cards { grid-template-columns: 1fr; }
      }
    `;
    document.head.appendChild(el);
  }, []);
  return null;
};

/* ─── Helpers ────────────────────────────────────────────────────── */
/* ─── Helpers ────────────────────────────────────────────────────── */
const getAuthHeader = (token) => {
  if (!token) return {};

  // JWT format
  if (token.split(".").length === 3) {
    return { Authorization: `Bearer ${token}` };
  }

  // DRF Token format
  return { Authorization: `Token ${token}` };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("User not logged in");
  }

  const headers = {
    ...getAuthHeader(token),

    ...(options.body && !(options.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),

    ...(options.headers || {}),
  };

  const config = {
    ...options,
    headers,
  };

  console.log("REQUEST TOKEN =", token);
  console.log("REQUEST HEADERS =", headers);

  const res = await fetch(url, config);

  if (res.status === 401) {
    localStorage.removeItem("token");
    throw new Error("Session expired. Please login again.");
  }

  if (!res.ok) {
    let msg = "Request failed";

    try {
      const err = await res.json();
      msg = err.detail || JSON.stringify(err);
    } catch {
      msg = await res.text();
    }

    throw new Error(msg);
  }

  return res.status === 204 ? null : res.json();
};

const getBillingModeColor = (mode) => {
  const map = { SERVICE: T.sky, PACKAGE: T.purple, CAMP_CASE: T.green, CAMP_AMOUNT: T.amber, MEDIBUDDY:"#0EA5E9", TCS_MONTHLY: "#2563EB", };
  return map[mode] || T.slate;
};

const getBillingModeBadgeClass = (mode) => {
  const map = { SERVICE: "badge-sky", PACKAGE: "badge-purple", CAMP_CASE: "badge-green", CAMP_AMOUNT: "badge-amber", MEDIBUDDY:"badge-sky", TCS_MONTHLY: "badge-red",};
  return map[mode] || "badge-slate";
};

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

/* ─── Reusable Components ────────────────────────────────────────── */
const Modal = ({ title, onClose, children, footer, wide }) => (
  <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className={`modal-box${wide ? " modal-box-wide" : ""}`}>
      <div className="modal-header">
        <span className="modal-title">{title}</span>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  </div>
);

const Field = ({ label, full, hint, children }) => (
  <div className={`form-group${full ? " full" : ""}`}>
    <label className="form-label">{label}</label>
    {children}
    {hint && <span className="form-hint">{hint}</span>}
  </div>
);

const Toast = ({ msg, type = "info" }) => (
  <div className={`toast toast-${type}`}><span>{msg}</span></div>
);

const SummaryCard = ({ label, value, sub, color }) => (
  <div className="summary-card">
    <div className="summary-card-label">{label}</div>
    <div className="summary-card-value" style={color ? { color } : {}}>{value}</div>
    {sub && <div className="summary-card-sub">{sub}</div>}
  </div>
);

const ServiceRateGrid = ({ rows, setRows }) => {

  const updateRate = (index, value) => {
    const copy = [...rows];

    copy[index] = {
      ...copy[index],
      rate_per_case: value,
    };

    setRows(copy);
  };

  return (
    <table className="service-rate-table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Rate / Case (₹)</th>
        </tr>
      </thead>

      <tbody>

        {rows.length === 0 && (
          <tr>
            <td
              colSpan={2}
              style={{
                textAlign: "center",
                padding: "24px",
              }}
            >
              No services found
            </td>
          </tr>
        )}

        {rows.map((row, index) => (
          <tr key={row.id}>

            <td className="service-name">
              {row.service_name}
            </td>

            <td>
              <input
                className="form-input service-rate-input"
                type="number"
                step="0.01"
                value={row.rate_per_case ?? ""}
                onChange={(e) =>
                  updateRate(index, e.target.value)
                }
              />
            </td>

          </tr>
        ))}

      </tbody>
    </table>
  );
};

/* ─── Billing Mode Selector ──────────────────────────────────────── */
const BillingModeSelector = ({ currentMode, onSelect }) => {
  const colorMap = {
    SERVICE:      { color: T.sky,    bg: "#EFF6FF", ring: "rgba(59,130,246,0.15)" },
    PACKAGE:      { color: T.purple, bg: "#F5F3FF", ring: "rgba(124,58,237,0.15)" },
    CAMP_CASE:    { color: T.green,  bg: "#F0FDF4", ring: "rgba(22,163,74,0.15)"  },
    CAMP_AMOUNT:  { color: T.amber,  bg: "#FFFBEB", ring: "rgba(217,119,6,0.15)"  },
    MEDIBUDDY:    { color:"#0EA5E9",   bg:"#F0F9FF",  ring:"rgba(14,165,233,.15)" },
    TCS_MONTHLY: { color: "#2563EB",bg: "#EFF6FF",ring: "rgba(37,99,235,.15)"},
  };
  return (
    <div className="mode-cards">
      {BILLING_MODES.map(m => {
        const c = colorMap[m.value];
        const selected = currentMode === m.value;
        return (
          <div
            key={m.value}
            className={`mode-card${selected ? " selected" : ""}`}
            style={{ "--mode-color": c.color, "--mode-bg": c.bg, "--mode-ring": c.ring }}
            onClick={() => onSelect(m.value)}
          >
            <div className="mode-card-icon">{m.icon}</div>
            <div className="mode-card-label">{m.label}</div>
            <div className="mode-card-desc">{m.desc}</div>
            <div className="mode-card-radio">
              {selected && <div className="mode-card-radio-dot" />}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Camp Case Config ───────────────────────────────────────────── */
const CampCaseConfig = ({ form, setForm }) => (
  <div className="config-panel">
    <div className="config-panel-title">🗂️ Camp Case MG Configuration</div>
    <div className="form-grid">
      <Field label="Minimum Camp Cases" hint="Total minimum cases guaranteed for the camp">
        <input className="form-input" type="number" placeholder="e.g. 500"
          value={form.full_camp_min_cases || ""}
          onChange={e => setForm(f => ({ ...f, full_camp_min_cases: e.target.value }))} />
      </Field>
      <Field label="Rate Per Extra Case (₹)" hint="Rate charged per case exceeding the minimum">
        <input className="form-input" type="number" step="0.01" placeholder="e.g. 250"
          value={form.full_camp_case_rate || ""}
          onChange={e => setForm(f => ({ ...f, full_camp_case_rate: e.target.value }))} />
      </Field>
    </div>
  </div>
);

/* ─── Camp Amount Config ─────────────────────────────────────────── */
const CampAmountConfig = ({ form, setForm }) => (
  <div className="config-panel">
    <div className="config-panel-title">💰 Camp Amount MG Configuration</div>
    <div className="form-grid">
      <Field label="Minimum Billing Amount (₹)" hint="Minimum total billing amount guaranteed for the camp" full>
        <input className="form-input" type="number" step="0.01" placeholder="e.g. 250000"
          value={form.full_camp_min_amount || ""}
          onChange={e => setForm(f => ({ ...f, full_camp_min_amount: e.target.value }))} />
      </Field>
    </div>
  </div>
);

/* ─── Medibuddy Config ─────────────────────────────────────────── */
const MedibuddyConfig = ({form,setForm}) => (
<div className="config-panel">
  <div className="config-panel-title">
    🏥 Medibuddy Configuration
  </div>

  <div className="form-grid">

    <Field
      label="Camp Day Charge"
      hint="Charge per utilized camp day"
      full
    >
      <input
        className="form-input"
        type="number"
        value={form.medibuddy_day_charge || ""}
        onChange={e =>
          setForm(f => ({
            ...f,
            medibuddy_day_charge: e.target.value
          }))
        }
      />
    </Field>

  </div>

  <div
    className="warn-banner"
    style={{marginTop:16}}
  >
    Dynamic slab case rate is auto calculated from backend pricing slabs.
    Location count removed — billing is driven only by cases + camp day charges.
  </div>

</div>
);

/* ─── Service Pricing Grid ───────────────────────────────────────── */
const ServicePricingGrid = ({ rows, setRows }) => (
  <div className="table-scroll">
    <table className="pricing-table">
      <thead>
        <tr><th>Service</th><th>Service MG</th><th>Rate Per Case (₹)</th></tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: T.steel }}>No services found</td></tr>
        )}
        {rows.map((row, i) => (
          <tr key={row.service_id || i}>
            <td style={{ fontWeight: 600 }}>{row.service_name}</td>
            <td>
              <input type="number" className="form-input" style={{ width: 130 }}
                value={row.service_mg || ""}
                onChange={e => { const r = [...rows]; r[i] = { ...r[i], service_mg: e.target.value }; setRows(r); }} />
            </td>
            <td>
              <input type="number" step="0.01" className="form-input" style={{ width: 160 }}
                value={row.rate_per_case || ""}
                onChange={e => { const r = [...rows]; r[i] = { ...r[i], rate_per_case: e.target.value }; setRows(r); }} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─── Package Pricing Grid ───────────────────────────────────────── */
const PackagePricingGrid = ({ rows, setRows }) => (
  <table className="pricing-table">
    <thead>
      <tr><th>Package</th><th>Package MG</th><th>Package Rate (₹)</th></tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: T.steel }}>No packages found</td></tr>
      )}
      {rows.map((row, i) => (
        <tr key={row.package_id || i}>
          <td style={{ fontWeight: 600 }}>{row.package_name}</td>
          <td>
            <input type="number" className="form-input" style={{ width: 130 }}
              value={row.package_mg || ""}
              onChange={e => { const r = [...rows]; r[i] = { ...r[i], package_mg: e.target.value }; setRows(r); }} />
          </td>
          <td>
            <input type="number" step="0.01" className="form-input" style={{ width: 160 }}
              value={row.package_rate || ""}
              onChange={e => { const r = [...rows]; r[i] = { ...r[i], package_rate: e.target.value }; setRows(r); }} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/* ─── Daily MG Editor ────────────────────────────────────────────── */
const DailyMGEditor = ({ rows, setRows }) => (
  <table className="pricing-table">
    <thead>
      <tr><th>Date</th><th>Daily MG (₹)</th></tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr><td colSpan={2} style={{ textAlign: "center", padding: 24, color: T.steel }}>No daily MG entries</td></tr>
      )}
      {rows.map((row, i) => (
        <tr key={i}>
          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{row.date}</td>
          <td>
            <input className="form-input" style={{ width: 140 }} value={row.daily_mg || ""}
              onChange={e => { const r = [...rows]; r[i] = { ...r[i], daily_mg: e.target.value }; setRows(r); }} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/* ─── Billing Patients Preview ───────────────────────────── */

const BillingPatientsPreview = ({
  campId,
  showToast,
  onDeleted
}) => {

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const user = useSelector((state) => state.auth.user);

  const isSupervisor =
    user?.groups?.some(
      (g) => g.name === "Supervisor" || g === "Supervisor"
    ) || false;


  const filteredPatients = patients.filter((p) => {

    const matchesSearch =
      p.patient_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesDate =
      !selectedDate ||
      p.test_date === selectedDate;

    return matchesSearch && matchesDate;
  });

  const loadPatients = async () => {

    try {

      setLoading(true);

      console.log(
        `${API}/billing-preview-patients/${campId}/`
      );
      const res = await apiRequest(
        `${API}/billing-preview-patients/${campId}/`
      );
      

      setPatients(res.patients || []);

    } catch (e) {

      showToast(e.message, "error");

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, [campId]);

  const deletePatient = async (patientId) => {

    const ok = window.confirm(
      "Delete this patient from billing?"
    );

    if (!ok) return;

    try {

      await apiRequest(
        `${API}/delete-patient/${patientId}/`,
        {
          method: "DELETE"
        }
      );

      showToast(
        "✓ Patient deleted",
        "success"
      );

      // reload patient list
      loadPatients();

      // reload billing preview
      if (onDeleted) {
        onDeleted();
      }

    } catch (e) {

      showToast(e.message, "error");
    }
  };

  return (
    <div style={{ marginTop: 24 }}>

      <div className="section-head">
        <span className="section-title">
          Billing Patients
        </span>
      </div>

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >

        <input
          type="text"
          placeholder="Search patient..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px 14px",
            width: "280px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            outline: "none",
            fontSize: "14px",
          }}
        />

        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{
            padding: "10px 14px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            outline: "none",
            fontSize: "14px",
          }}
        />

        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setSearchTerm("");
            setSelectedDate("");
          }}
        >
          Clear Filters
        </button>

      </div>

      {loading ? (
        <div className="empty-state">
          Loading patients...
        </div>
      ) : filteredPatients.length === 0 ? (
        <div className="empty-state">
          No patients found
        </div>
      ) : (
        <div
          className="table-scroll"
          style={{
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <table
            className="pricing-table"
            style={{
              minWidth: "1100px",
              width: "100%",
            }}
          >

            <thead>
              <tr>
                <th>Sr No</th>
                <th>Patient</th>
                <th>Test Date</th>
                <th>Completed</th>
                <th>Pending</th>
                <th>Completed Services</th>
                <th>Pending Services</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>

              {filteredPatients.map((p, index) => (

                <tr key={p.id}>

                  <td>{index + 1}</td>

                  <td style={{ fontWeight: 600 }}>
                    {p.patient_name}
                  </td>

                  <td>
                    {p.test_date
                      ? new Date(p.test_date).toLocaleDateString("en-IN")
                      : "—"}
                  </td>

                  <td>{p.completed_count}</td>

                  <td>{p.pending_count}</td>

                  <td>
                    {(p.completed_services || []).join(", ") || "—"}
                  </td>

                  <td>
                    {(p.pending_services || []).join(", ") || "—"}
                  </td>

                  <td>

                    {isSupervisor ? (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deletePatient(p.id)}
                      >
                        Delete
                      </button>
                    ) : (
                      "—"
                    )}

                  </td>

                </tr>
              ))}

            </tbody>

          </table>
        </div>
      )}

    </div>
  );
};

/* ─── Billing Preview ────────────────────────────────────────────── */
const BillingPreview = ({ preview, summary, onRecalculate, campId, user }) => {

  const isSupervisor = user?.groups?.some((g) => {
    if (typeof g === "string") {
      return g.toLowerCase() === "supervisor";
    }

    return g?.name?.toLowerCase() === "supervisor";
  });

  const isMedibuddy = summary?.billing_mode === "MEDIBUDDY";
  const billingMode = summary?.billing_mode || "SERVICE";
  const isPackage = billingMode === "PACKAGE";

  const [override, setOverride] = useState({
    extra_consumables: "",
    travel_charges: "",
    misc_charges: ""
  });


  // ✅ FETCH EXISTING OVERRIDE
  useEffect(() => {
    if (!campId) return;

    apiRequest(`${API}/billing-camps/${campId}/billing-override/`)
      .then(res => {
        if (res.data) setOverride(res.data);
      })
      .catch(() => {});
  }, [campId]);

  // ✅ SAVE OVERRIDE
  const saveOverride = async () => {
    await apiRequest(
      `${API}/billing-camps/${campId}/billing-override/`,
      {
        method: "POST", // or PATCH (depends on backend)
        body: JSON.stringify(override),
      }
    );

    onRecalculate && onRecalculate();
  };

  // =========================
  // 🔵 MEDIBUDDY UI
  // =========================

  if (isMedibuddy) {
    return (
      <div>

        <div className="summary-cards">
          {/* <SummaryCard
            label="Actual Cases"
            value={renderEditableField(
              summary.actual_cases,
              "actual_cases_override",
              summary.actual_cases
            )}
          /> */}

          <SummaryCard
            label="Actual Cases"
            value={summary.actual_cases}
          />

          <SummaryCard label="Camp Days" value={summary.camp_days} />

          <SummaryCard
            label="Slab Rate"
            value={`₹${summary.slab_rate}`}
          />

          <SummaryCard
            label="Final Total"
            value={fmt(summary.final_total)}
            color={T.green}
          />
        </div>

        {/* ✅ Supervisor Adjustments */}
        {isSupervisor && (
          <div style={{
            marginTop: 20,
            padding: 16,
            border: "1px dashed #CBD5E1",
            borderRadius: 10
          }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Supervisor Adjustments
            </div>

            {[
              ["extra_consumables", "Extra Consumables"],
              ["travel_charges", "Travel Charges"],
              ["misc_charges", "Misc Charges"]
            ].map(([key, label]) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label>{label}</label>
                <input
                  type="number"
                  value={override[key] ?? ""}
                  onChange={(e) =>
                    setOverride({
                      ...override,
                      [key]: e.target.value
                    })
                  }
                  style={{ marginLeft: 10 }}
                />
              </div>
            ))}

            <button className="btn btn-primary" onClick={saveOverride}>
              Save Override
            </button>
          </div>
        )}

        <div className="section-head">
          <span className="section-title">
            Medibuddy Billing Components
          </span>
        </div>

        <table className="pricing-table">
          <thead>
            <tr>
              <th>Component</th>
              <th>Units</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>

          <tbody>
            {preview.map((row, i) => (
              <tr key={i}>
                <td>{row.component}</td>
                <td>{row.units}</td>
                <td>{fmt(row.rate)}</td>
                <td>{fmt(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{
          marginTop: 20,
          padding: 18,
          background: "#F0F9FF",
          borderRadius: 12
        }}>
          <div style={{ fontWeight: 700 }}>
            Applied Pricing Logic
          </div>

          150 cases fell into slab  
          ₹{summary.slab_rate} per case.
        </div>

        <div className="preview-total">
          <div>
            <div className="preview-total-label">
              Final Invoice
            </div>
          </div>

          <div className="preview-total-amount">
            {fmt(summary.final_total)}
          </div>
        </div>

      </div>
    );
  }

  // =========================
  // 🔵 SERVICE / PACKAGE UI
  // =========================

  return (
    <div>

      <div className="summary-cards">
        {/* <SummaryCard
          label="Actual Cases"
          value={renderEditableField(
            summary?.actual_cases,
            "actual_cases_override",
            summary?.actual_cases
          )}
        /> */}

        <SummaryCard
          label="Actual Cases"
          value={summary?.actual_cases}
        />

        <SummaryCard
          label="MG Extra Cases"
          value={`+${summary?.service_mg_extra ?? 0}`}
          color={T.amber}
        />

        <SummaryCard
          label="Daily MG Extra"
          value={`+${summary?.daily_mg_extra ?? 0}`}
          color={T.purple}
        />

        <SummaryCard
          label="Subtotal"
          value={fmt(summary?.subtotal)}
          color={T.navy}
        />
      </div>

      {/* ✅ Supervisor Adjustments */}
      {isSupervisor && (
        <div style={{
          marginTop: 20,
          padding: 16,
          border: "1px dashed #CBD5E1",
          borderRadius: 10
        }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>
            Supervisor Adjustments
          </div>

          {[
            ["extra_consumables", "Extra Consumables"],
            ["travel_charges", "Travel Charges"],
            ["misc_charges", "Misc Charges"]
          ].map(([key, label]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <label>{label}</label>
              <input
                type="number"
                value={override[key] ?? ""}
                onChange={(e) =>
                  setOverride({
                    ...override,
                    [key]: e.target.value
                  })
                }
                style={{ marginLeft: 10 }}
              />
            </div>
          ))}

          <button className="btn btn-primary" onClick={saveOverride}>
            Save Override
          </button>
        </div>
      )}

      <div className="section-head">
        <span className="section-title">
          {isPackage ? "Package Billing Detail" : "Service Billing Detail"}
        </span>

        {/* {onRecalculate && (
          <button className="btn btn-ghost btn-sm" onClick={onRecalculate}>
            ↺ Recalculate
          </button>
        )} */}
      </div>

      <table className="pricing-table">
        <thead>
          <tr>
            <th>{isPackage ? "Package" : "Service"}</th>
            <th>Actual</th>
            <th>MG Extra</th>
            <th>Billable</th>
            <th>Rate</th>
            <th>Amount</th>
            <th>Source</th>
          </tr>
        </thead>

        <tbody>
          {(preview || []).map((row, i) => (
            <tr key={row.service_id || row.package_id || i}>
              <td style={{ fontWeight: 600 }}>
                {row.service_name || row.package_name}
              </td>

              <td>{row.actual_cases}</td>

              <td style={{
                fontWeight: 700,
                color: row.service_mg_extra > 0 ? T.amber : T.steel
              }}>
                +{row.service_mg_extra ?? 0}
              </td>

              <td style={{ fontWeight: 700, color: T.blue }}>
                {row.billable_cases}
              </td>

              <td style={{ fontFamily: "DM Mono, monospace" }}>
                ₹{row.rate}
              </td>

              <td style={{
                fontWeight: 700,
                fontFamily: "DM Mono, monospace"
              }}>
                ₹{row.amount}
              </td>

              <td>
                <span className={`badge badge-sky`}>
                  {row.pricing_source}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="preview-total">
        <div>
          <div className="preview-total-label">
            Final Bill Amount
          </div>
          <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
            After all MG adjustments
          </div>
        </div>

        <div className="preview-total-amount">
          {fmt(summary?.final_total)}
        </div>
      </div>

    </div>
  );
};


const ClientMedibuddyBillScreen = ({ onBack, showToast }) => {

  const CLIENT_ID = 57;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // API CALL (FIXED - previously missing)
  const getClientMedibuddyBill = async (start, end) => {
    return apiRequest(
      `${API}/client-medibuddy-bill/${CLIENT_ID}/?start_date=${start}&end_date=${end}`
    );
  };

  // LOAD DATA
  const load = async () => {
    try {
      if (!startDate || !endDate) {
        showToast("Please select date range", "error");
        return;
      }

      if (startDate > endDate) {
        showToast("Start date cannot be after end date", "error");
        return;
      }

      setLoading(true);

      const res = await getClientMedibuddyBill(startDate, endDate);

      setData(res);

    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={onBack}>Camps</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-current">
          Client Billing — {data?.client || "Select Range"}
        </span>
      </div>

      <div className="page-card">

        {/* Header */}
        <div className="page-card-header">
          <span className="page-card-title">
            Medibuddy Client Billing
          </span>
        </div>

        <div className="page-card-body">

          {/* 🔥 DATE RANGE FILTER */}
          <div style={{
            display: "flex",
            gap: 10,
            marginBottom: 20,
            flexWrap: "wrap",
            alignItems: "center"
          }}>

            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <button
              className="btn btn-primary"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Loading..." : "🔍 Fetch Bill"}
            </button>

          </div>

          {/* 🔄 STATES */}
          {loading && (
            <div className="empty-state">Loading...</div>
          )}

          {!loading && !data && (
            <div className="empty-state">
              Select a date range and fetch billing data
            </div>
          )}

          {/* 📊 DATA RENDER */}
          {!loading && data && (
            <div>

              {/* CAMP LEVEL */}
              {data.details.map((camp, i) => (
                <div key={i} style={{ marginBottom: 24 }}>

                  <div style={{ marginBottom: 10 }}>

                    <div style={{
                      fontWeight: 700,
                      fontSize: 16
                    }}>
                      {camp.camp_name}
                    </div>

                    {data.camp_dates?.[i] && (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#666"
                        }}
                      >
                        Camp Duration:
                        <b> {data.camp_dates[i].start_date}</b>
                        {" → "}
                        <b>{data.camp_dates[i].end_date}</b>
                      </div>
                    )}

                  </div>

                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Units</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {camp.details.map((row, j) => (
                        <tr key={j}>
                          <td>{row.component}</td>
                          <td>{row.units}</td>
                          <td>{fmt(row.rate)}</td>
                          <td>{fmt(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Cases: <b>{camp.summary.actual_cases}</b> |
                    Days: <b>{camp.summary.camp_days}</b> |
                    Total: <b>{fmt(camp.summary.final_total)}</b>
                  </div>

                  <hr className="divider" />

                </div>
              ))}

              {/* CLIENT SUMMARY */}
              <div className="preview-total">
                <div>
                  <div className="preview-total-label">
                    Client Total
                  </div>
                  <div style={{ fontSize: 12 }}>
                    Cases: {data.summary.total_cases} | Camp Days: {data.summary.total_days}
                  </div>
                </div>

                <div className="preview-total-amount">
                  {fmt(data.summary.final_total)}
                </div>
              </div>

            </div>
          )}

        </div>

        <div className="sticky-footer">
          <button className="btn btn-ghost" onClick={onBack}>
            ← Back
          </button>
        </div>

      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════════════════════════
   SCREEN 2 — Camp Billing Configuration
══════════════════════════════════════════════════════════════════ */
const BillingConfigScreen = ({ camp, onBack, showToast }) => {
  const [config, setConfig]         = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [origMode, setOrigMode]     = useState(null);
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [showWarn, setShowWarn]     = useState(false);

  // Pricing sub-modal state
  const [pricingModal, setPricingModal]   = useState(false);
  const [pricingRows, setPricingRows]     = useState([]);
  const [dailyMgModal, setDailyMgModal]   = useState(false);
  const [dailyMgRows, setDailyMgRows]     = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [serviceRateModal, setServiceRateModal] = useState(false);
  const [serviceRateRows, setServiceRateRows] = useState([]);
  const [serviceRateLoading, setServiceRateLoading] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const data = await apiRequest(`${API}/billing-camps/${camp.id}/billing-config/`);
      setConfig(data);
      setSelectedMode(data.billing_mode || null);
      setOrigMode(data.billing_mode || null);
      setForm({
        full_camp_min_cases:data.full_camp_min_cases||"",
        full_camp_case_rate:data.full_camp_case_rate||"",
        full_camp_min_amount:data.full_camp_min_amount||"",
        medibuddy_day_charge:
          data.medibuddy_day_charge || 500
      });
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleModeSelect = (mode) => {
    if (mode !== origMode && origMode) setShowWarn(true);
    else setShowWarn(false);
    setSelectedMode(mode);
  };

  const openServiceRate = async () => {
    try {
      setServiceRateLoading(true);

      const res = await apiRequest(
        `${API}/tcs/camp/${camp.id}/service-rate/`
      );

      setServiceRateRows(res.services || []);
      setServiceRateModal(true);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setServiceRateLoading(false);
    }
  };

  const saveServiceRate = async () => {
    try {
      await apiRequest(
        `${API}/tcs/camp/${camp.id}/service-rate/`,
        {
          method: "PATCH",
          body: JSON.stringify({
            services: serviceRateRows,
          }),
        }
      );

      showToast("✓ Service rates updated", "success");
      setServiceRateModal(false);
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const payload = { billing_mode: selectedMode };
      if (selectedMode === "CAMP_CASE") {
        payload.full_camp_min_cases = Number(form.full_camp_min_cases);
        payload.full_camp_case_rate  = Number(form.full_camp_case_rate);
      }
      if (selectedMode === "CAMP_AMOUNT") {
        payload.full_camp_min_amount = Number(form.full_camp_min_amount);
      }
      if(selectedMode==="MEDIBUDDY"){
      payload.medibuddy_location_count=
        Number(form.medibuddy_location_count)

      payload.medibuddy_day_charge=
        Number(form.medibuddy_day_charge)
      }
      await apiRequest(`${API}/billing-camps/${camp.id}/billing-mode-update/`, {
        method: "POST", body: JSON.stringify(payload),
      });
      setOrigMode(selectedMode);
      setShowWarn(false);
      showToast("✓ Billing configuration saved", "success");
    } catch (e) { showToast(e.message, "error"); }
    finally { setSaving(false); }
  };

  /* Pricing helpers */
  const openPricing = async () => {
    try {
      setPricingLoading(true);
      const data = await apiRequest(`${API}/billing-camps/${camp.id}/pricing/`);
      setPricingRows(data);
      setPricingModal(true);
    } catch (e) { showToast(e.message, "error"); }
    finally { setPricingLoading(false); }
  };

  const savePricing = async () => {
    try {
      await apiRequest(`${API}/billing-camps/${camp.id}/pricing-update/`, {
        method: "POST", body: JSON.stringify(pricingRows),
      });
      showToast("✓ Pricing updated", "success");
      setPricingModal(false);
    } catch (e) { showToast(e.message, "error"); }
  };

  const openDailyMg = async () => {
    try {
      const data = await apiRequest(`${API}/billing-camps/${camp.id}/daily-mg/`);
      setDailyMgRows(data);
      setDailyMgModal(true);
    } catch (e) { showToast(e.message, "error"); }
  };

  const saveDailyMg = async () => {
    try {
      await apiRequest(`${API}/billing-camps/${camp.id}/daily-mg-update/`, {
        method: "POST", body: JSON.stringify(dailyMgRows),
      });
      showToast("✓ Daily MG saved", "success");
      setDailyMgModal(false);
    } catch (e) { showToast(e.message, "error"); }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={onBack}>Camps</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-current">Billing Config — {camp.branch_name}</span>
      </div>

      <div className="page-card">
        <div className="page-card-header">
          <span className="page-card-title">Camp Billing Configuration</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: T.steel }}>{camp.branch_name} · {camp.location}</span>
          </div>
        </div>
        <div className="page-card-body">
          {showWarn && (
            <div className="warn-banner">
              <span className="warn-banner-icon">⚠️</span>
              <span>Changing billing mode may affect existing pricing configuration. Previous pricing data may be reset.</span>
            </div>
          )}

          <div style={{ marginBottom: 12, fontSize: 13, color: T.steel, fontWeight: 500 }}>
            Select one billing mode for this camp:
          </div>

          <BillingModeSelector currentMode={selectedMode} onSelect={handleModeSelect} />

          {/* Mode-specific config panels */}
          {selectedMode === "SERVICE" && (
            <div className="config-panel">
              <div className="config-panel-title">🔬 Service Level Configuration</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="btn btn-sky" onClick={openDailyMg} disabled={pricingLoading}>
                  📅 Daily MG Setup
                </button>
                <button className="btn btn-primary" onClick={openPricing} disabled={pricingLoading}>
                  🔬 Service Pricing Setup
                </button>
              </div>
            </div>
          )}

          {selectedMode === "PACKAGE" && (
            <div className="config-panel">
              <div className="config-panel-title">📦 Package Level Configuration</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-purple" onClick={openPricing} disabled={pricingLoading}>
                  📦 Package Pricing Setup
                </button>
              </div>
            </div>
          )}

          {selectedMode === "CAMP_CASE" && (
            <CampCaseConfig form={form} setForm={setForm} />
          )}

          {selectedMode === "CAMP_AMOUNT" && (
            <CampAmountConfig form={form} setForm={setForm} />
          )}

          {selectedMode==="MEDIBUDDY" && (
          <MedibuddyConfig
            form={form}
            setForm={setForm}
          />
          )}

          {selectedMode === "TCS_MONTHLY" && (
            <div className="config-panel">
              <div className="config-panel-title">
                🏢 TCS Monthly Billing Configuration
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="btn btn-primary"
                  onClick={openServiceRate}
                  disabled={serviceRateLoading}
                >
                  💰 Configure Service Rates
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Save */}
        <div className="sticky-footer">
          <button className="btn btn-ghost" onClick={onBack}>← Back to Camps</button>
          <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Configuration"}
          </button>
        </div>
      </div>

      {/* Service / Package Pricing Modal */}
      {pricingModal && (
        <Modal
          title={selectedMode === "PACKAGE" ? `Package Pricing — ${camp.branch_name}` : `Service Pricing — ${camp.branch_name}`}
          onClose={() => setPricingModal(false)}
          wide
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setPricingModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePricing}>Save Pricing</button>
            </>
          }
        >
          {selectedMode === "PACKAGE"
            ? <PackagePricingGrid rows={pricingRows} setRows={setPricingRows} />
            : <ServicePricingGrid rows={pricingRows} setRows={setPricingRows} />
          }
        </Modal>
      )}

      {/* Daily MG Modal */}
      {dailyMgModal && (
        <Modal
          title={`Daily MG — ${camp.branch_name}`}
          onClose={() => setDailyMgModal(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDailyMgModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDailyMg}>Save Daily MG</button>
            </>
          }
        >
          <DailyMGEditor rows={dailyMgRows} setRows={setDailyMgRows} />
        </Modal>
      )}

      {serviceRateModal && (
        <Modal
          title={`Service Rates — ${camp.branch_name}`}
          wide
          onClose={() => setServiceRateModal(false)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setServiceRateModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={saveServiceRate}
              >
                Save Rates
              </button>
            </>
          }
        >
          <ServiceRateGrid
            rows={serviceRateRows}
            setRows={setServiceRateRows}
          />
        </Modal>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN 4 — Bill Preview Page (full screen)
══════════════════════════════════════════════════════════════════ */
const BillPreviewScreen = ({ camp, onBack, showToast }) => {
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [generating, setGen]    = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [billHistory, setBillHistory] = useState([]);
  const user = useSelector((state) => state.auth.user);
  console.log("USER =", user);
  

  useEffect(() => { loadPreview(); }, []);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`${API}/preview-bill/${camp.id}/`);
      setPreview(data);
    } catch (e) { showToast(e.message, "error"); }
    finally { setLoading(false); }
  };

  const recalculate = async () => {
    try {
      const data = await apiRequest(`${API}/billing-camps/${camp.id}/recalculate-preview/`, { method: "POST" });
      setPreview(data);
      showToast("✓ Preview recalculated", "success");
    } catch (e) { showToast(e.message, "error"); }
  };

  const generateBill = async () => {
    try {
      setGen(true);
      await apiRequest(`${API}/generate-bill/${camp.id}/`, { method: "POST" });
      showToast("✓ Bill generated successfully", "success");
    } catch (e) { showToast(e.message, "error"); }
    finally { setGen(false); }
  };

  const openHistory = async () => {
    try {
      const data = await apiRequest(`${API}/camp-bills/${camp.id}/`);
      setBillHistory(data.results || data);
      setHistoryOpen(true);
    } catch (e) { showToast(e.message, "error"); }
  };

  return (
    <div>
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={onBack}>Camps</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-current">Bill Preview — {camp.branch_name}</span>
      </div>

      <div className="page-card">
        <div className="page-card-header">
          <span className="page-card-title">Billing Preview</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-amber btn-sm" onClick={openHistory}>📄 History</button>
            <button className="btn btn-green btn-sm" onClick={generateBill} disabled={generating}>
              {generating ? "Generating…" : "⚡ Generate Bill"}
            </button>
          </div>
        </div>
        <div className="page-card-body">
          {loading ? (
            <div className="empty-state">
              <div className="empty-state-icon">⏳</div>
              <div className="empty-state-text">
                Loading preview…
              </div>
            </div>
          ) : preview ? (

            <>
              <BillingPreview
                preview={preview.preview}
                summary={preview.summary}
                onRecalculate={recalculate}
                campId={camp.id}
                user={user}
              />

              <BillingPatientsPreview
                campId={camp.id}
                showToast={showToast}
                onDeleted={loadPreview}
              />
            </>

          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <div className="empty-state-text">
                No preview available
              </div>
            </div>
          )}
        </div>
        <div className="sticky-footer">
          <button className="btn btn-ghost" onClick={onBack}>← Back to Camps</button>
        </div>
      </div>

      {historyOpen && (
        <Modal title="Bill History" onClose={() => setHistoryOpen(false)}
          footer={<button className="btn btn-ghost" onClick={() => setHistoryOpen(false)}>Close</button>}>
          {billHistory.length === 0
            ? <div className="empty-state"><div className="empty-state-icon">📄</div><div className="empty-state-text">No bill versions found</div></div>
            : billHistory.map(v => (
              <div className="history-item" key={v.id}>
                <span className="history-version">Version {v.version}</span>
                <span className="history-amount">{fmt(v.total_amount)}</span>
              </div>
            ))
          }
        </Modal>
      )}
    </div>
  );
};



const getBillingModeLabel = (mode) => {
 const found = BILLING_MODES.find(
   m => m.value === mode
 );

 return found?.label || "None";
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN 1 — Billing Camps Dashboard
══════════════════════════════════════════════════════════════════ */
const CampsDashboard = ({ onConfigureBilling, onPreviewBill, onClientMedibuddy, showToast }) => {
  const [camps, setCamps]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [modalOpen, setModalOpen]   = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);

  const emptyForm = {
    client_name: "", location: "", branch_code: "", branch_name: "",
    district: "", state: "", pin_code: "",
    start_date: "", end_date: "",
    billing_mode: "",
    full_camp_min_cases: "", full_camp_min_amount: "",
    contract_file: null, quotation_file: null,
  };
  const [form, setForm] = useState(emptyForm);

  const fetchCamps = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`${API}/billing-camps/`);
      setCamps(data.results || data);
    } catch (e) { showToast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCamps(); }, [fetchCamps]);

  const submitCamp = async () => {
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {

        // files
        if (
          k === "contract_file" ||
          k === "quotation_file"
        ) {
          if (v) fd.append(k, v);
          return;
        }

        // important fix for None billing mode
        if (
          k === "billing_mode" &&
          v === null
        ) {
          fd.append("billing_mode", "");
          return;
        }

        if (v !== null && v !== undefined) {
          fd.append(k, v);
        }
      });
      if (editingCamp) {
        await apiRequest(`${API}/billing-camps/${editingCamp.id}/`, { method: "PATCH", body: fd });
        showToast("✓ Camp updated", "success");
      } else {
        await apiRequest(`${API}/billing-camps/`, { method: "POST", body: fd });
        showToast("✓ Camp created", "success");
      }
      setModalOpen(false);
      fetchCamps();
    } catch (e) { showToast(e.message, "error"); }
  };

  const isActive = (camp) => {
    const today = new Date().toISOString().split("T")[0];
    return camp.start_date <= today && today <= camp.end_date;
  };

  const filtered = camps.filter(c => {
    const matchSearch =
      (c.branch_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.location || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.client_name || "").toLowerCase().includes(search.toLowerCase());
    const matchMode   = !filterMode   || c.billing_mode === filterMode;
    const matchStatus = !filterStatus || (filterStatus === "ACTIVE" ? isActive(c) : !isActive(c));
    return matchSearch && matchMode && matchStatus;
  });

  const F = (key, label, type = "text", opts = {}) => (
    <Field label={label} full={opts.full}>
      {type === "file" ? (
        <input
          className="form-input"
          type="file"
          accept={opts.accept}
          onChange={(e) =>
            setForm(f => ({
              ...f,
              [key]: e.target.files?.[0] || null
            }))
          }
        />
      ) : (
        <input
          className="form-input"
          type={type}
          value={form[key] || ""}
          onChange={(e) =>
            setForm(f => ({
              ...f,
              [key]: e.target.value
            }))
          }
        />
      )}

      {/* optional show existing uploaded file while editing */}
      {type === "file" && editingCamp?.[key] && (
        <div
          style={{
            marginTop:8,
            fontSize:12,
            color:"#64748B"
          }}
        >
          Existing file attached
        </div>
      )}
    </Field>
  );

  return (
    <div>
      {/* Header */}
      <div className="camps-header">
        <h1 className="camps-title">
          <span className="camps-title-dot" />
          Camps Management
        </h1>
        <div className="camps-controls">
          <input className="camps-search" placeholder="Search camps…" value={search}
            onChange={e => setSearch(e.target.value)} />
          <select className="filter-select" value={filterMode}
            onChange={e => setFilterMode(e.target.value)}>
            <option value="">All Billing Modes</option>
            {BILLING_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select className="filter-select" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="CLOSED">Closed</option>
          </select>
          <button
            className="btn btn-green"
            onClick={onClientMedibuddy}
          >
            🏥 Medibuddy Client Billing
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="camps-table-wrap">
        <table className="camps-table">
          <thead>
            <tr>
              <th>Client / Branch</th>
              <th>Location</th>
              <th>Dates</th>
              <th>Billing Mode</th>
              <th>Min Cases</th>
              <th>Min Amount</th>
              <th>Status</th>
              <th>Configure</th>
              <th>Bills</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="loading-row"><td colSpan={10}>Loading camps…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">
                    <div className="empty-state-icon">🏕️</div>
                    <div className="empty-state-text">No camps found</div>
                  </div>
                </td>
              </tr>
            ) : filtered.map(camp => {
              const active = isActive(camp);
              return (
                <tr key={camp.id}>
                  <td>
                    <div className="camp-name">{camp.branch_name}</div>
                    <div className="camp-location">{camp.client_name}</div>
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>{camp.location}</div>
                    <div className="camp-location">{camp.district}{camp.state ? `, ${camp.state}` : ""}</div>
                  </td>
                  <td>
                    <div className="date-range">
                      {camp.start_date}<br />
                      <span style={{ color: T.steel }}>→ {camp.end_date}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      camp.billing_mode
                        ? getBillingModeBadgeClass(camp.billing_mode)
                        : "badge-slate"
                    }`}>
                      {getBillingModeLabel(camp.billing_mode)}
                    </span>
                  </td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                    {camp.full_camp_min_cases || "—"}
                  </td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                    {camp.full_camp_min_amount ? fmt(camp.full_camp_min_amount) : "—"}
                  </td>
                  <td>
                    <span className={`badge ${active ? "badge-active" : "badge-closed"}`}>
                      {active ? "Active" : "Closed"}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sky btn-sm" onClick={() => onConfigureBilling(camp)}>
                      ⚙️ Configure
                    </button>
                  </td>
                  <td>
                    <div className="action-group">
                      <button className="btn btn-slate btn-sm" onClick={() => onPreviewBill(camp)}>
                        Preview
                      </button>
                    </div>
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      setEditingCamp(camp);
                      setForm({
                        client_name: camp.client_name || "", location: camp.location || "",
                        branch_code: camp.branch_code || "", branch_name: camp.branch_name || "",
                        district: camp.district || "", state: camp.state || "",
                        pin_code: camp.pin_code || "", start_date: camp.start_date || "",
                        end_date: camp.end_date || "", billing_mode: camp.billing_mode || "",
                        full_camp_min_cases: camp.full_camp_min_cases || "",
                        full_camp_min_amount: camp.full_camp_min_amount || "",
                        contract_file: null, quotation_file: null,
                      });
                      setModalOpen(true);
                    }}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Camp Create/Edit Modal */}
      {modalOpen && (
        <Modal
          title={editingCamp ? "Edit Camp" : "Add New Camp"}
          onClose={() => setModalOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitCamp}>
                {editingCamp ? "Update Camp" : "Create Camp"}
              </button>
            </>
          }
        >
          <div className="form-grid">
            {F("client_name", "Client")}
            {F("location", "Location")}
            {F("branch_code", "Branch Code")}
            {F("branch_name", "Branch Name")}
            {F("district", "District")}
            {F("state", "State")}
            {F("pin_code", "PIN Code")}
            <Field label="Billing Mode">
              <select
                className="form-select"
                value={form.billing_mode || ""}
                onChange={e =>
                  setForm(f => ({
                    ...f,
                    billing_mode:
                        e.target.value === "NULL"
                          ? null
                          : e.target.value
                  }))
                  }
              >
                <option value="NULL">None</option>

                {BILLING_MODES.map(m => (
                  <option
                    key={m.value}
                    value={m.value}
                  >
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
            {F("start_date", "Start Date", "date")}
            {F("end_date", "End Date", "date")}
            {form.billing_mode === "CAMP_CASE" && F("full_camp_min_cases", "Min Camp Cases")}
            {form.billing_mode === "CAMP_AMOUNT" && F("full_camp_min_amount", "Min Camp Amount (₹)")}
            {F("contract_file",  "Contract File",  "file", { accept: ".pdf,.doc,.docx" })}
            {F("quotation_file", "Quotation File", "file", { accept: ".pdf,.doc,.docx" })}
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   ROOT — App Shell with routing
══════════════════════════════════════════════════════════════════ */
export default function CampsPage() {
  const [screen, setScreen]     = useState("dashboard"); // dashboard | config | preview
  const [activeCamp, setActiveCamp] = useState(null);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3400);
  };

  const goConfig  = (camp) => { setActiveCamp(camp); setScreen("config"); };
  const goPreview = (camp) => { setActiveCamp(camp); setScreen("preview"); };
  const goDash    = ()     => { setActiveCamp(null);  setScreen("dashboard"); };
  const goClientMedibuddy = () => {
    setActiveCamp(null);   // not camp-based
    setScreen("client_medibuddy");
  };

  return (
    <>
      <GlobalStyle />
      <div className="camps-root">
        {toast && <Toast msg={toast.msg} type={toast.type} />}

        {screen === "dashboard" && (
          <CampsDashboard
            onConfigureBilling={goConfig}
            onPreviewBill={goPreview}
            onClientMedibuddy={goClientMedibuddy} 
            showToast={showToast}
          />
        )}

        {screen === "config" && activeCamp && (
          <BillingConfigScreen
            camp={activeCamp}
            onBack={goDash}
            showToast={showToast}
          />
        )}

        {screen === "preview" && activeCamp && (
          <BillPreviewScreen
            camp={activeCamp}
            onBack={goDash}
            showToast={showToast}
          />
        )}
        {screen === "client_medibuddy" && (
          <ClientMedibuddyBillScreen
            onBack={goDash}
            showToast={showToast}
          />
        )}
      </div>
    </>
  );
}